import { Injectable, inject } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { map, catchError, mergeMap } from 'rxjs/operators';

import { Project, ProjectCampaignResponse, ProjectCreate, ProjectPdfExportPayload } from '@core/models/projects.model';
import { ProjectsApiService } from './api/projects-api.service';

@Injectable({
    providedIn: 'root',
})
export class ProjectsService {
    private projectsApi = inject(ProjectsApiService);

    loadProjects(): Observable<Project[]> {
        return this.projectsApi.getProjects().pipe(
            map((response) => {
                if (!Array.isArray(response)) {
                    return [];
                }
                return response;
            }),
            catchError(() => of([])),
        );
    }

    loadProjectCampaigns(campaignIds: string[], batchLimit: number = 3): Observable<ProjectCampaignResponse> {
        return this.loadAllBatches(campaignIds, batchLimit).pipe(
            map((response) => {
                const aggregatedMetrics = this.aggregateMetricsByType(response.datas_only_metrics_by_type);

                return {
                    ...response,
                    datas_only_metrics_by_type: aggregatedMetrics,
                } as ProjectCampaignResponse;
            }),
            catchError(() =>
                of({
                    datas: [],
                    datas_only_metrics_by_type: [],
                    campaign_ids: [],
                }),
            ),
        );
    }

    private loadAllBatches(campaignIds: string[], limit: number): Observable<any> {
        const totalCampaigns = campaignIds.length;

        const getAllBatches = (offset: number = 0, allCampaigns: any[] = [], allMetricsByType: any[] = []): Observable<any> => {
            if (offset >= totalCampaigns) {
                return of({
                    datas: allCampaigns,
                    datas_only_metrics_by_type: allMetricsByType,
                    campaign_ids: campaignIds,
                });
            }

            return this.projectsApi.getProjectCampaignsBatch(campaignIds, offset, limit).pipe(
                mergeMap((batch) => {
                    const campaigns = batch.datas || [];
                    const batchMetrics = batch.datas_only_metrics_by_type || [];

                    const updatedCampaigns = [...allCampaigns, ...campaigns];
                    const updatedMetrics = [...allMetricsByType, ...batchMetrics];

                    return of(null).pipe(
                        delay(1000), // Rate limiting
                        mergeMap(() => getAllBatches(offset + limit, updatedCampaigns, updatedMetrics)),
                    );
                }),
            );
        };

        return getAllBatches();
    }

    private aggregateMetricsByType(metrics: any[]): any[] {
        const groupedMetrics = metrics.reduce<Record<string, any[]>>((acc, metric) => {
            const type = metric.type;
            if (!acc[type]) {
                acc[type] = [];
            }
            acc[type].push(metric);
            return acc;
        }, {});

        return Object.entries(groupedMetrics).map(([type, metricsGroup]) => {
            if (metricsGroup.length === 1) {
                return metricsGroup[0];
            }

            const aggregated: any = { type };

            const allKeys = new Set<string>();
            metricsGroup.forEach((metric) => {
                Object.keys(metric).forEach((key) => allKeys.add(key));
            });

            allKeys.forEach((key) => {
                if (key === 'type') return;

                const firstNonNullValue = metricsGroup.find((m) => m[key] !== undefined && m[key] !== null)?.[key];

                if (typeof firstNonNullValue === 'number') {
                    aggregated[key] = 0;
                } else {
                    aggregated[key] = firstNonNullValue;
                }
            });

            metricsGroup.forEach((metric) => {
                Object.keys(metric).forEach((key) => {
                    if (key === 'type') return;

                    const value = metric[key];
                    if (typeof value === 'number' && typeof aggregated[key] === 'number') {
                        if (!key.includes('rate') && key !== 'cpm' && key !== 'cpc' && key !== 'cost_per_video') {
                            aggregated[key] += value;
                        }
                    }
                });
            });

            this.recalculateDerivedMetrics(aggregated);

            return aggregated;
        });
    }

    private recalculateDerivedMetrics(aggregated: any): void {
        if (aggregated.impressions && aggregated.clicks) {
            aggregated.ctr = Number(((aggregated.clicks / aggregated.impressions) * 100).toFixed(2));
        } else if ('ctr' in aggregated) {
            aggregated.ctr = 0;
        }

        if (aggregated.spent && aggregated.impressions && aggregated.impressions > 0) {
            aggregated.cpm = Number(((aggregated.spent / aggregated.impressions) * 1000).toFixed(2));
        }

        if (aggregated.spent && aggregated.clicks && aggregated.clicks > 0) {
            aggregated.cpc = Number((aggregated.spent / aggregated.clicks).toFixed(2));
        }

        if (aggregated.spent && aggregated.youtube_view && aggregated.youtube_view > 0) {
            aggregated.cost_per_video = Number((aggregated.spent / aggregated.youtube_view).toFixed(2));
        }

        if (aggregated.ca !== undefined && aggregated.spent) {
            if (aggregated.spent > 0) {
                aggregated.roas = Number((aggregated.ca / aggregated.spent).toFixed(2));
            } else {
                aggregated.roas = 0;
            }
        }

        if (aggregated.completions && aggregated.starts && aggregated.starts > 0) {
            aggregated.completion_rate = Number(((aggregated.completions / aggregated.starts) * 100).toFixed(2));
        }

        if (aggregated.skips && aggregated.starts && aggregated.starts > 0) {
            aggregated.skip_rate = Number(((aggregated.skips / aggregated.starts) * 100).toFixed(2));
        }

        if (aggregated.starts && aggregated.impressions && aggregated.impressions > 0) {
            aggregated.start_rate = Number(((aggregated.starts / aggregated.impressions) * 100).toFixed(2));
        }

        if (aggregated.youtube_view && aggregated.impressions && aggregated.impressions > 0) {
            aggregated.youtube_view_rate = Number(((aggregated.youtube_view / aggregated.impressions) * 100).toFixed(2));
        }

        if (aggregated.conversions !== undefined && aggregated.clicks) {
            if (aggregated.clicks > 0) {
                aggregated.convs_rate = Number(((aggregated.conversions / aggregated.clicks) * 100).toFixed(2));
            } else {
                aggregated.convs_rate = 0;
            }
        }
    }

    createProject(projectData: ProjectCreate): Observable<number> {
        return this.projectsApi.createProject(projectData).pipe(
            map((projectId) => projectId),
            catchError((error) => {
                throw error;
            }),
        );
    }

    updateProject(projectId: number, projectData: ProjectCreate): Observable<{ success: boolean; message: string }> {
        return this.projectsApi.updateProject(projectId, projectData).pipe(
            catchError((error) => {
                throw error;
            }),
        );
    }

    deleteProject(projectId: number): Observable<void> {
        return this.projectsApi.deleteProject(projectId).pipe(
            map(() => {}),
            catchError((error) => {
                throw error;
            }),
        );
    }

    exportProjectReportPdf(body: ProjectPdfExportPayload): Observable<Blob> {
        return this.projectsApi.exportProjectReportPdf(body);
    }
}
